// frontend/src/pages/EmpleadosPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Página de gestión de cajeros/trabajadores para el Dueño/Admin.
// El dueño crea, edita y activa/desactiva los cajeros de cada sucursal.
// Autenticación directa de cajero: Usuario + Contraseña.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import { empleadosAPI, sucursalesAPI } from '../services/api';
import './EmpleadosPage.css';

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('es-PE', { day:'2-digit', month:'short', year:'numeric' }) : '—';

// Mapeo de valor DB → etiqueta legible para el dueño.
const ROL_LABELS = {
    vendedora:  { label: 'Técnica farmacéutica',    icon: '💉', color: '#818cf8' },
    encargada:  { label: 'Encargada de sucursal',   icon: '💼', color: '#34d399' },
};
const getRolInfo = (rol) =>
    ROL_LABELS[rol] ?? { label: 'Sin rol asignado', icon: '➖', color: '#475569' };

// ── Componente principal ──────────────────────────────────────────────────────
const EmpleadosPage = () => {
    const [empleados,  setEmpleados]  = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [isSaving,   setIsSaving]   = useState(false);
    const [toast,      setToast]      = useState(null);
    const [showModal,  setShowModal]  = useState(false);
    const [editTarget, setEditTarget] = useState(null); // null=crear, obj=editar

    const emptyForm = {
        nombre: '',
        usuario: '',
        password: '',
        password2: '',
        sucursales_ids: [],
        todas_sucursales: false,
        rol: 'vendedora',
        activo: true
    };
    const [form, setForm] = useState(emptyForm);

    // ── Carga inicial ─────────────────────────────────────────────────────────
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [empRes, sucRes] = await Promise.all([
                empleadosAPI.getAll(),
                sucursalesAPI.getAll(),
            ]);
            setEmpleados(empRes.data?.data?.empleados  || []);
            const sAll = sucRes.data?.data?.sucursales || [];
            // Solo sucursales activas para el selector del modal
            setSucursales(sAll.filter(s => s.activo));
        } catch {
            showToast('Error al cargar datos', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // ── Toast ─────────────────────────────────────────────────────────────────
    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3500);
    };

    // ── Stats ─────────────────────────────────────────────────────────────────
    const totalActivos  = empleados.filter(e => e.activo).length;
    const totalInactivos = empleados.length - totalActivos;

    // ── Modal helpers ─────────────────────────────────────────────────────────
    const openCreate = () => {
        setEditTarget(null);
        const defaultIds = sucursales.length > 0 ? [sucursales[0].id] : [];
        setForm({
            ...emptyForm,
            sucursales_ids: defaultIds,
            todas_sucursales: false
        });
        setShowModal(true);
    };

    const openEdit = (e) => {
        setEditTarget(e);
        let ids = [];
        if (Array.isArray(e.sucursales_ids) && e.sucursales_ids.length > 0) {
            ids = e.sucursales_ids.map(Number);
        } else if (Array.isArray(e.sucursales_asignadas) && e.sucursales_asignadas.length > 0) {
            ids = e.sucursales_asignadas.map(s => Number(s.id));
        } else if (e.sucursal_id) {
            ids = [Number(e.sucursal_id)];
        }

        const isAll = Boolean(e.todas_sucursales) || (sucursales.length > 0 && ids.length === sucursales.length);

        setForm({
            nombre: e.nombre,
            usuario: e.usuario || '',
            password: '',
            password2: '',
            sucursales_ids: isAll ? sucursales.map(s => s.id) : ids,
            todas_sucursales: isAll,
            rol: e.rol || 'vendedora',
            activo: e.activo !== false
        });
        setShowModal(true);
    };

    const closeModal = () => { setShowModal(false); setEditTarget(null); setForm(emptyForm); };

    const handleToggleSucursalCheckbox = (sucId) => {
        setForm(prev => {
            const current = prev.sucursales_ids || [];
            const exists = current.includes(sucId);
            const next = exists ? current.filter(id => id !== sucId) : [...current, sucId];
            return {
                ...prev,
                sucursales_ids: next,
                todas_sucursales: next.length === sucursales.length && sucursales.length > 0
            };
        });
    };

    const handleToggleTodasSucursales = (checked) => {
        setForm(prev => ({
            ...prev,
            todas_sucursales: checked,
            sucursales_ids: checked ? sucursales.map(s => s.id) : (sucursales[0] ? [sucursales[0].id] : [])
        }));
    };

    // ── Guardar ───────────────────────────────────────────────────────────────
    const handleSave = async (ev) => {
        ev.preventDefault();

        if (!form.nombre.trim()) return showToast('El nombre es requerido', 'error');

        const finalIds = form.todas_sucursales ? sucursales.map(s => s.id) : (form.sucursales_ids || []);
        if (finalIds.length === 0) return showToast('Selecciona al menos una sucursal para el trabajador', 'error');

        // Contraseña obligatoria al crear; al editar es opcional (para cambiar)
        if (!editTarget && !form.password) return showToast('La contraseña es requerida', 'error');
        if (form.password && form.password !== form.password2) return showToast('Las contraseñas no coinciden', 'error');

        setIsSaving(true);
        try {
            const payload = {
                nombre: form.nombre.trim(),
                usuario: form.usuario.trim() || undefined,
                sucursales_ids: finalIds,
                todas_sucursales: Boolean(form.todas_sucursales),
                sucursal_id: finalIds[0],
                rol: form.rol || 'vendedora',
                activo: Boolean(form.activo)
            };
            if (form.password) {
                payload.password = form.password;
                payload.pin = form.password; // retrocompatibilidad
            }

            if (editTarget) {
                await empleadosAPI.update(editTarget.id, payload);
                showToast('✅ Trabajador actualizado correctamente');
            } else {
                await empleadosAPI.create(payload);
                showToast('✅ Trabajador creado correctamente');
            }
            closeModal();
            load();
        } catch (err) {
            showToast(`❌ ${err.response?.data?.message || 'Error al guardar'}`, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // ── Toggle activo (ON/OFF) ────────────────────────────────────────────────
    const handleToggle = async (emp) => {
        const nuevoEstado = !emp.activo;
        const accion = nuevoEstado ? 'reactivar' : 'desactivar';
        if (!window.confirm(`¿Seguro que deseas ${accion} a "${emp.nombre}"?`)) return;
        try {
            await empleadosAPI.update(emp.id, { activo: nuevoEstado });
            showToast(`Trabajador ${nuevoEstado ? 'activado (ON)' : 'desactivado (OFF)'}`);
            load();
        } catch {
            showToast('Error al cambiar el estado', 'error');
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="emp-page" style={{ width: '100%', maxWidth: '100%' }}>

            {/* Header */}
            <div className="emp-header">
                <div>
                    <h1>👤 Gestión de Cajeros y Personal</h1>
                    <p>Administra los accesos de tus trabajadores por sucursal. Cada trabajador ingresa con su usuario y contraseña en 1 solo paso.</p>
                </div>
                <button
                    className="emp-btn-new"
                    onClick={openCreate}
                    disabled={sucursales.length === 0}
                    id="btn-nuevo-cajero"
                >
                    + Nuevo Trabajador
                </button>
            </div>

            {/* Aviso si no hay sucursales */}
            {sucursales.length === 0 && !loading && (
                <div className="emp-alert-no-sucursales">
                    ⚠️ No tienes sucursales activas registradas.
                    Crea primero una sucursal en el menú de <strong>Sucursales</strong> para poder asignarle trabajadores.
                </div>
            )}

            {/* Stats Bar */}
            <div className="emp-stats-bar">
                <div className="emp-stat-card">
                    <div className="emp-stat-icon">👥</div>
                    <div>
                        <div className="emp-stat-num">{empleados.length}</div>
                        <div className="emp-stat-label">Total Trabajadores</div>
                    </div>
                </div>
                <div className="emp-stat-card">
                    <div className="emp-stat-icon">🟢</div>
                    <div>
                        <div className="emp-stat-num">{totalActivos}</div>
                        <div className="emp-stat-label">Activos (ON)</div>
                    </div>
                </div>
                <div className="emp-stat-card">
                    <div className="emp-stat-icon">⏸</div>
                    <div>
                        <div className="emp-stat-num">{totalInactivos}</div>
                        <div className="emp-stat-label">Desactivados (OFF)</div>
                    </div>
                </div>
            </div>

            {/* Tabla de Empleados */}
            {loading ? (
                <div className="emp-loading">
                    <div className="emp-spinner" />
                    <p>Cargando personal...</p>
                </div>
            ) : empleados.length === 0 ? (
                <div className="emp-empty">
                    <div className="emp-empty-icon">👤</div>
                    <h3>No hay trabajadores registrados</h3>
                    <p>Crea a tus cajeras y encargadas para que puedan iniciar sesión directamente con su usuario y contraseña.</p>
                    {sucursales.length > 0 && (
                        <button className="emp-btn-new" onClick={openCreate}>
                            + Crear primer trabajador
                        </button>
                    )}
                </div>
            ) : (
                <div className="emp-table-card">
                    <div className="emp-table-wrapper">
                        <table className="emp-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Trabajador / Usuario</th>
                                    <th>Sucursal Asignada</th>
                                    <th className="center">Rol</th>
                                    <th className="center">Estado (ON/OFF)</th>
                                    <th className="center">Fecha Registro</th>
                                    <th className="center">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {empleados.map((e, index) => {
                                    const isAllBranches = Boolean(e.todas_sucursales) || 
                                        (sucursales.length > 0 && e.sucursales_asignadas && e.sucursales_asignadas.length === sucursales.length && sucursales.length > 1);
                                    const hasMultiple = e.sucursales_asignadas && e.sucursales_asignadas.length > 1;

                                    return (
                                        <tr key={e.id} style={{ opacity: e.activo ? 1 : 0.6 }}>
                                            <td style={{ color: '#64748b', fontSize: 13, fontWeight: '700' }}>#{index + 1}</td>
                                            <td>
                                                <div className="emp-nombre">{e.nombre}</div>
                                                <div className="emp-pin-hint">
                                                    👤 Usuario: <strong>{e.usuario || e.nombre.toLowerCase().replace(/\s+/g, '_')}</strong>
                                                </div>
                                            </td>
                                            <td>
                                                {isAllBranches ? (
                                                    <span className="emp-badge-rotativo all" title={sucursales.map(s => s.nombre).join(', ')}>
                                                        🌟 Todas las sucursales ({sucursales.length})
                                                    </span>
                                                ) : hasMultiple ? (
                                                    <span className="emp-badge-rotativo" title={e.sucursales_asignadas.map(s => s.nombre).join(', ')}>
                                                        🔄 Rotativo ({e.sucursales_asignadas.length} sucursales)
                                                        <small style={{ display: 'block', fontSize: 11, opacity: 0.85, marginTop: 2 }}>
                                                            {e.sucursales_asignadas.map(s => s.nombre).join(', ')}
                                                        </small>
                                                    </span>
                                                ) : (
                                                    <span className={`emp-badge ${e.sucursal_es_offline ? 'offline' : 'online'}`}>
                                                        {e.sucursal_es_offline ? '📴' : '🌐'} {e.sucursal_nombre || e.sucursales_asignadas?.[0]?.nombre || 'Sucursal principal'}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="center">
                                                {(() => {
                                                    const { label, icon, color } = getRolInfo(e.rol);
                                                    return (
                                                        <span style={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 4,
                                                            fontSize: 12, fontWeight: 600,
                                                            padding: '3px 10px', borderRadius: 20,
                                                            background: `${color}18`,
                                                            color,
                                                            border: `1px solid ${color}40`,
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            {icon} {label}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="center">
                                                <button
                                                    onClick={() => handleToggle(e)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        border: 'none',
                                                        borderRadius: '20px',
                                                        padding: '4px 12px',
                                                        fontWeight: '700',
                                                        fontSize: '12px',
                                                        background: e.activo ? '#10b98120' : '#ef444420',
                                                        color: e.activo ? '#10b981' : '#ef4444',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                    }}
                                                    title="Haz clic para cambiar estado ON / OFF"
                                                >
                                                    <span>{e.activo ? '🟢 ON' : '🔴 OFF'}</span>
                                                    <span>{e.activo ? 'Activo' : 'Inactivo'}</span>
                                                </button>
                                            </td>
                                            <td className="center" style={{ fontSize: 13, color: '#64748b' }}>
                                                {formatDate(e.created_at)}
                                            </td>
                                            <td className="center">
                                                <div className="emp-actions">
                                                    <button
                                                        className="emp-action-btn"
                                                        onClick={() => openEdit(e)}
                                                        id={`btn-edit-cajero-${e.id}`}
                                                        title="Editar datos, contraseña o sucursal"
                                                    >
                                                        ✏️ Editar
                                                    </button>
                                                    <button
                                                        className={`emp-action-btn ${e.activo ? 'danger' : 'restore'}`}
                                                        onClick={() => handleToggle(e)}
                                                        id={`btn-toggle-cajero-${e.id}`}
                                                    >
                                                        {e.activo ? 'Desactivar' : 'Reactivar'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── MODAL ──────────────────────────────────────────────────────── */}
            {showModal && (
                <div className="emp-overlay" onClick={closeModal}>
                    <div className="emp-modal" onClick={e => e.stopPropagation()}>
                        <div className="emp-modal-header">
                            <h3>{editTarget ? '✏️ Editar Trabajador' : '👤 Nuevo Trabajador'}</h3>
                            <button className="emp-modal-close" onClick={closeModal}>✕</button>
                        </div>

                        <form onSubmit={handleSave}>
                            {/* Nombre */}
                            <div className="emp-form-group">
                                <label>Nombre y Apellidos *</label>
                                <input
                                    id="emp-input-nombre"
                                    required
                                    autoFocus
                                    value={form.nombre}
                                    onChange={e => setForm({ ...form, nombre: e.target.value })}
                                    placeholder="Ej: María García"
                                />
                            </div>

                            {/* Usuario de Acceso */}
                            <div className="emp-form-group">
                                <label>Usuario para Iniciar Sesión *</label>
                                <input
                                    id="emp-input-usuario"
                                    required
                                    value={form.usuario}
                                    onChange={e => setForm({ ...form, usuario: e.target.value.toLowerCase().replace(/\s+/g, '') })}
                                    placeholder="Ej: maria_cusco"
                                />
                                <div className="emp-pin-note">💡 Este es el usuario con el que iniciará sesión en el sistema.</div>
                            </div>

                            {/* Sucursales Asignadas (Soporte Rotativo / Múltiples) */}
                            <div className="emp-form-group">
                                <label>Sucursales Autorizadas *</label>
                                {sucursales.length > 1 ? (
                                    <div className="emp-sucursales-container">
                                        <label className={`emp-sucursal-item ${form.todas_sucursales ? 'selected' : ''}`} style={{ fontWeight: 700 }}>
                                            <input
                                                type="checkbox"
                                                id="emp-check-todas"
                                                checked={form.todas_sucursales}
                                                onChange={e => handleToggleTodasSucursales(e.target.checked)}
                                            />
                                            <span>🌟 Todas las sucursales (Personal Rotativo)</span>
                                        </label>

                                        {!form.todas_sucursales && sucursales.map(s => {
                                            const isChecked = (form.sucursales_ids || []).includes(s.id);
                                            return (
                                                <label
                                                    key={s.id}
                                                    className={`emp-sucursal-item ${isChecked ? 'selected' : ''}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={() => handleToggleSucursalCheckbox(s.id)}
                                                    />
                                                    <span>{s.es_offline ? '📴' : '🌐'} {s.nombre}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.05)', borderRadius: 10, color: '#f1f5f9', fontSize: 14 }}>
                                        {sucursales[0]?.es_offline ? '📴' : '🌐'} {sucursales[0]?.nombre || 'Sucursal Principal'}
                                    </div>
                                )}
                                <div className="emp-pin-note">
                                    💡 {sucursales.length > 1 ? 'Puedes marcar una o varias sucursales donde este trabajador pueda atender.' : 'Sucursal asignada automáticamente.'}
                                </div>
                            </div>

                            {/* Rol */}
                            <div className="emp-form-group">
                                <label>Rol en la sucursal</label>
                                <select
                                    id="emp-select-rol"
                                    value={form.rol}
                                    onChange={e => setForm({ ...form, rol: e.target.value })}
                                >
                                    <option value="vendedora">💉 Técnica farmacéutica (Cajera)</option>
                                    <option value="encargada">💼 Encargada de sucursal</option>
                                </select>
                            </div>

                            {/* Estado ON / OFF */}
                            <div className="emp-form-group">
                                <label>Estado del Cajero (ON / OFF)</label>
                                <select
                                    id="emp-select-activo"
                                    value={form.activo ? 'true' : 'false'}
                                    onChange={e => setForm({ ...form, activo: e.target.value === 'true' })}
                                >
                                    <option value="true">🟢 ON - Activo (Puede ingresar al sistema)</option>
                                    <option value="false">🔴 OFF - Desactivado (Acceso bloqueado)</option>
                                </select>
                            </div>

                            {/* Contraseña */}
                            <div className="emp-form-group">
                                <label>
                                    Contraseña de Acceso {editTarget ? '(dejar vacío para no cambiar)' : '*'}
                                </label>
                                <input
                                    id="emp-input-pass"
                                    type="password"
                                    value={form.password}
                                    onChange={e => setForm({ ...form, password: e.target.value })}
                                    placeholder="Contraseña personal del trabajador"
                                    required={!editTarget}
                                />
                            </div>

                            {/* Confirmar Contraseña */}
                            {form.password && (
                                <div className="emp-form-group">
                                    <label>Confirmar Contraseña *</label>
                                    <input
                                        id="emp-input-pass2"
                                        type="password"
                                        value={form.password2}
                                        onChange={e => setForm({ ...form, password2: e.target.value })}
                                        placeholder="Repite la contraseña"
                                        style={{
                                            borderColor: form.password2 && form.password !== form.password2
                                                ? '#ef4444' : ''
                                        }}
                                    />
                                    {form.password2 && form.password !== form.password2 && (
                                        <div className="emp-pin-note" style={{ color: '#f87171' }}>
                                            ❌ Las contraseñas no coinciden
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="emp-form-actions">
                                <button type="button" className="emp-btn-cancel" onClick={closeModal}>
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="emp-btn-save"
                                    disabled={isSaving || (form.pin && form.pin !== form.pin2)}
                                >
                                    {isSaving
                                        ? '⏳ Guardando...'
                                        : editTarget ? '💾 Guardar cambios' : '✅ Crear Cajero'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Toast */}
            {toast && <div className={`emp-toast ${toast.type}`}>{toast.message}</div>}
        </div>
    );
};

export default EmpleadosPage;
